import type { TicketDetail, TicketReason, TicketRow, TicketState } from "@/api/tickets";
import type { StatusTone } from "@/components/shared/status-badge";
import { formatSoon, formatWaiting, parseIstDateTime } from "@/lib/format";

/**
 * How a ticket reads on every surface — the list, the conversation header,
 * the panel. One place, so "Needs reply" never means two things.
 */

/** The one badge a ticket carries: whose move it is. */
export const STATE_LABEL: Record<TicketState, string> = {
  needs_reply: "Needs reply",
  follow_up_due: "Follow-up due",
  ai_answering: "AI answering",
  waiting: "Waiting on customer",
  stale: "Old",
  closed: "Closed",
};

export const STATE_TONE: Record<TicketState, StatusTone> = {
  needs_reply: "warning",
  follow_up_due: "info",
  ai_answering: "info",
  waiting: "neutral",
  stale: "neutral",
  closed: "success",
};

/** Why it is in someone's To do — the row's second line. */
export const REASON_LABEL: Record<TicketReason, string> = {
  new: "New",
  customer_replied: "Customer replied",
  ai_handed_over: "AI handed over",
  reopened: "Reopened",
  follow_up_due: "Follow-up due",
};

export const REASON_TONE: Record<TicketReason, StatusTone> = {
  new: "warning",
  customer_replied: "warning",
  ai_handed_over: "warning",
  reopened: "warning",
  follow_up_due: "info",
};

/** The AI hand-over codes in words (ticket_ai_events.code). */
export const HAND_OVER_LABEL: Record<string, string> = {
  asked_for_person: "Customer asked for a person",
  complaint: "A complaint to look into",
  needs_staff: "Needs someone from the team",
  not_understood: "The AI did not understand",
  no_answer: "The AI had no answer",
  error: "The AI ran into an error",
  person_replied: "A person replied",
  other: "Handed over",
};

export type WaitTone = "normal" | "amber" | "red";

/** Used when a row or detail carries no target (an older server). */
const DEFAULT_TARGET_MINUTES = 30;

/**
 * How late a wait is against the ticket's reply target: amber from half the
 * target, red once it is over (the category's target — "Reply within 30 min").
 */
export function waitToneOf(
  since: number | null,
  now: number,
  targetMinutes: number = DEFAULT_TARGET_MINUTES,
): WaitTone {
  if (since === null) return "normal";
  const waited = now - since;
  const target = targetMinutes * 60_000;
  if (waited > target) return "red";
  if (waited >= target / 2) return "amber";
  return "normal";
}

/** "30 min", "2 h", "1 h 30 min". */
export function formatTarget(minutes: number): string {
  if (minutes < 60) return `${minutes} min`;
  const hours = Math.floor(minutes / 60);
  const rest = minutes % 60;
  return rest ? `${hours} h ${rest} min` : `${hours} h`;
}

/** Why a ticket was closed, in the words of the Close dialog. */
export const CLOSE_REASON_LABEL: Record<string, string> = {
  resolved: "Resolved",
  no_response: "Customer didn’t respond",
  duplicate: "Duplicate",
  not_valid: "Not valid",
  other: "Other",
  stale: "No reply for 30 days",
};

/** "2h 05m" style: how long the customer has waited. */
export function waitedFor(since: number, now: number): string {
  const seconds = Math.max(0, Math.floor((now - since) / 1000));
  return seconds < 60 ? "Just now" : formatWaiting(seconds);
}

/**
 * A follow-up as epoch ms. The column is IST text: "YYYY-MM-DD HH:mm:ss", or
 * a bare date on old rows, read as the end of that day.
 */
export function followUpMs(value: string | null): number | null {
  if (!value) return null;
  const full = parseIstDateTime(value);
  if (full !== null) return full;
  if (/^\d{4}-\d{2}-\d{2}$/.test(value.trim())) {
    const ms = Date.parse(`${value.trim()}T23:59:59+05:30`);
    return Number.isNaN(ms) ? null : ms;
  }
  return null;
}

/** "Today, 4:00 PM" / "Tomorrow, 10:00 AM" / "07/10/2026, 4:30 PM". */
export function followUpLabel(value: string | null, now: number = Date.now()): string {
  const ms = followUpMs(value);
  return ms === null ? "No follow-up" : formatSoon(ms, now);
}

/** Epoch ms → "YYYY-MM-DD HH:mm:ss" in IST, as the API takes a follow-up. */
export function toIstDateTime(ms: number): string {
  const ist = new Date(ms + 5.5 * 3_600_000);
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${ist.getUTCFullYear()}-${pad(ist.getUTCMonth() + 1)}-${pad(ist.getUTCDate())} ${pad(ist.getUTCHours())}:${pad(ist.getUTCMinutes())}:00`;
}

/** The IST wall-clock hour and weekday of a moment. */
function istParts(ms: number) {
  const ist = new Date(ms + 5.5 * 3_600_000);
  return { hour: ist.getUTCHours(), weekday: ist.getUTCDay() };
}

/** Epoch ms of `daysAhead` days from now at `hour`:00 IST. */
function atIstHour(now: number, daysAhead: number, hour: number): number {
  const ist = new Date(now + 5.5 * 3_600_000);
  const midnightUtcOfIstDay = Date.UTC(
    ist.getUTCFullYear(),
    ist.getUTCMonth(),
    ist.getUTCDate() + daysAhead,
  );
  return midnightUtcOfIstDay - 5.5 * 3_600_000 + hour * 3_600_000;
}

export interface FollowUpPreset {
  key: string;
  label: string;
  at: number;
}

/**
 * The follow-ups people pick most: in an hour, later today, tomorrow
 * morning, Monday morning. Work runs 6 AM to 6 PM IST and Thursday is the
 * day off, so "tomorrow" on a Wednesday means Friday.
 */
export function followUpPresets(now: number): FollowUpPreset[] {
  const presets: FollowUpPreset[] = [
    { key: "1h", label: "In 1 hour", at: now + 3_600_000 },
    { key: "3h", label: "In 3 hours", at: now + 3 * 3_600_000 },
  ];
  const { hour, weekday } = istParts(now);
  if (hour < 16)
    presets.push({ key: "today", label: "Today, 5 PM", at: atIstHour(now, 0, 17) });
  // Thursday is the day off: Wednesday's "tomorrow" is Friday.
  const tomorrowSkip = weekday === 3 ? 2 : 1;
  presets.push({
    key: "tomorrow",
    label: weekday === 3 ? "Friday, 10 AM" : "Tomorrow, 10 AM",
    at: atIstHour(now, tomorrowSkip, 10),
  });
  const toMonday = (8 - weekday) % 7 || 7;
  if (toMonday > tomorrowSkip) {
    presets.push({
      key: "monday",
      label: "Monday, 10 AM",
      at: atIstHour(now, toMonday, 10),
    });
  }
  return presets;
}

/** The line under a row's name: why it needs you, else its state. */
export function rowReasonLabel(row: TicketRow): string {
  if (row.reason && (row.state === "needs_reply" || row.state === "follow_up_due")) {
    return REASON_LABEL[row.reason];
  }
  return STATE_LABEL[row.state];
}

export function rowReasonTone(row: TicketRow): StatusTone {
  if (row.reason && (row.state === "needs_reply" || row.state === "follow_up_due")) {
    return REASON_TONE[row.reason];
  }
  return STATE_TONE[row.state];
}

/** Sarvam's codes in words, for "what was said". */
export const LANGUAGE_NAME: Record<string, string> = {
  "en-IN": "English",
  "hi-IN": "Hindi",
  "kn-IN": "Kannada",
  "ta-IN": "Tamil",
  "te-IN": "Telugu",
  "ml-IN": "Malayalam",
  "mr-IN": "Marathi",
  "bn-IN": "Bengali",
  "gu-IN": "Gujarati",
  "pa-IN": "Punjabi",
  "od-IN": "Odia",
};

/** Job statuses (bang_common `CustomerJobStatus`) in staff words. */
export const JOB_STATUS_LABEL: Record<string, string> = {
  scheduled: "Scheduled",
  not_done: "Not done",
  done: "Done",
  not_at_home: "Not at home",
  reported: "Reported",
  under_review: "Under review",
  rescheduled: "Rescheduled",
  extended: "Extended",
  cancelled: "Cancelled",
};

export const JOB_STATUS_TONE: Record<string, StatusTone> = {
  done: "success",
  scheduled: "info",
  not_done: "destructive",
  reported: "warning",
  under_review: "warning",
  not_at_home: "neutral",
  rescheduled: "neutral",
  extended: "neutral",
  cancelled: "neutral",
};

export const SERVICE_LABEL: Record<string, string> = {
  exterior: "Exterior",
  full: "Full cleaning",
  interior: "Interior",
  other: "Other",
};

/** The person's other tickets that are still open — what this one can merge into. */
export function mergeTargetsOf(ticket: TicketDetail) {
  return ticket.state === "closed"
    ? []
    : ticket.otherTickets.filter((other) => other.state !== "closed");
}
